import {
  IAcumuladosRow,
  IPatientHistory,
} from '@app/core/patient-requests';
import {
  formatPatientRequestDateTime,
  parsePatientRequestDateTime,
  PatientRequestDateTimeParts,
} from '../patient-request-date-format.util';

/** Punto de la serie para una prueba en una petición. */
export interface EvolutionChartPoint {
  requestId: string;
  externalId: string;
  dateTime: string;
  result: number | null;
  upperLimit: number | null;
  lowerLimit: number | null;
  normalRange?: string;
}

/** Una prueba con su serie de puntos (una por petición). */
export interface EvolutionChartTest {
  testCode: number;
  testName: string;
  unit: string;
  points: EvolutionChartPoint[];
}

/** Datos del paciente para la tabla de la pantalla de gráficas. */
export interface EvolutionChartsPatient {
  id: string;
  name: string;
  lastName: string;
  birthDate: string;
  sex: string;
  age?: number;
}

/** Payload para el componente de gráficas de evolución. */
export interface EvolutionChartsPayload {
  patient: EvolutionChartsPatient;
  tests: EvolutionChartTest[];
}

/** RequisNr de 8 dígitos YYYYMMDD que coincide con el día de la muestra: duplica la fecha en el eje X. */
function requisNrRedundantWithEntryDate(requisNr: string, entry: PatientRequestDateTimeParts): boolean {
  const s = String(requisNr ?? '').trim();
  if (!/^\d{8}$/.test(s)) return false;
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(4, 6));
  const day = Number(s.slice(6, 8));
  if (m < 1 || m > 12 || day < 1 || day > 31) return false;
  const fromId = new Date(y, m - 1, day);
  if (
    fromId.getFullYear() !== y ||
    fromId.getMonth() !== m - 1 ||
    fromId.getDate() !== day
  ) {
    return false;
  }
  return (
    entry.year === fromId.getFullYear() &&
    entry.month === fromId.getMonth() + 1 &&
    entry.day === fromId.getDate()
  );
}

/**
 * Eje X gráficas evolución (WIR-EF-03.02.6 / #523): identificador de petición + fecha/hora DD/MM/AA hh:mm.
 * Si el SP envía fecha/hora textual con zona (`...Z`), se preserva la hora textual y no se convierte a hora local.
 * Si RequisNr es YYYYMMDD y coincide con la fecha de entrada, no se muestra (evita "20160412" junto a 12/04/16);
 * en ese caso se usa ExtRequisNr si viene informado.
 */
export function formatEvolutionChartAxisLabel(
  requestId: string,
  dateTimeRaw: string,
  externalId?: string
): string {
  const idPrimary = String(requestId ?? '').trim();
  const idExt = String(externalId ?? '').trim();
  const raw = String(dateTimeRaw ?? '').trim();
  if (!raw) {
    return idPrimary || idExt || '—';
  }
  const parts = parsePatientRequestDateTime(raw);
  if (!parts) {
    const head = idPrimary || idExt;
    return head ? `${head} ${raw}` : raw;
  }
  const datePart = formatPatientRequestDateTime(raw, 2);

  const primaryRedundant = idPrimary && requisNrRedundantWithEntryDate(idPrimary, parts);
  let lineId = '';
  if (idPrimary && !primaryRedundant) {
    lineId = idPrimary;
  } else if (primaryRedundant && idExt) {
    lineId = idExt;
  }

  if (lineId) {
    return `${lineId}\n${datePart}`;
  }
  return datePart;
}

export function evolutionRequestIdsMatch(a: string | number | null | undefined, b: string | number | null | undefined): boolean {
  const left = String(a ?? '').trim();
  const right = String(b ?? '').trim();
  if (!left || !right) return false;
  if (left === right) return true;
  const leftNum = Number(left);
  const rightNum = Number(right);
  return !Number.isNaN(leftNum) && !Number.isNaN(rightNum) && leftNum === rightNum;
}

const TITLE_RES_TYPES = new Set([10, 11, 12]);

/** Misma convención que acumulados-table-view: el API puede devolver PascalCase o camelCase. */
function getAcumField(r: IAcumuladosRow, pascal: string): unknown {
  const row = r as unknown as Record<string, unknown>;
  return row[pascal] ?? row[pascal.charAt(0).toLowerCase() + pascal.slice(1)];
}

function toText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
}

function toNumericValue(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const trimmed = v.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(',', '.');
  if (!/^[+-]?\d+(?:\.\d+)?$/.test(normalized)) return null;
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

function toLimit(value: unknown): number | null {
  return toNumericValue(value);
}

function buildPatientFromHistory(p: IPatientHistory): EvolutionChartsPatient {
  let birthDate = '';
  if (p.fechaNac instanceof Date) {
    birthDate = p.fechaNac.toISOString().slice(0, 10);
  } else {
    birthDate = String(p.fechaNac ?? '');
  }
  return {
    id: String(p.historia ?? ''),
    name: String(p.nombre ?? ''),
    lastName: String(p.apellidos ?? ''),
    birthDate,
    sex: String(p.sexo ?? ''),
    age: p.edad,
  };
}

function processOneRow(
  r: IAcumuladosRow,
  reqByCol: Map<number, { requisNr: string; extRequisNr: string; entryDate: string }>,
  testMeta: Map<number, { testNr: number; testName: string; units: string; printSeq: number }>,
  pointMap: Map<string, { result: number | null; upperLimit: number | null; lowerLimit: number | null; normalRange?: string }>
): void {
  const requisCol = Number(getAcumField(r, 'RequisCol') ?? 0);
  const testRow = Number(getAcumField(r, 'TestRow') ?? 0);
  const testNr = Number(getAcumField(r, 'TestNr') ?? 0);
  const printSeqRaw = getAcumField(r, 'PrintSeq');
  const resType = Number(getAcumField(r, 'ResType') ?? 0);

  if (requisCol > 0 && !reqByCol.has(requisCol)) {
    reqByCol.set(requisCol, {
      requisNr: toText(getAcumField(r, 'RequisNr')),
      extRequisNr: toText(getAcumField(r, 'ExtRequisNr')),
      entryDate: toText(getAcumField(r, 'EntryDate')),
    });
  }
  if (!testMeta.has(testRow)) {
    testMeta.set(testRow, {
      testNr,
      testName: toText(getAcumField(r, 'TestName')),
      units: toText(getAcumField(r, 'Units')),
      printSeq: printSeqRaw == null ? 0 : Number(printSeqRaw),
    });
  }
  const isTitleRow = TITLE_RES_TYPES.has(resType);
  if (isTitleRow) {
    return;
  }
  const key = `${testRow}-${requisCol}`;
  const normalRange = toText(getAcumField(r, 'NormalRange')).trim();
  pointMap.set(key, {
    result: toNumericValue(getAcumField(r, 'Result')),
    upperLimit: normalRange ? toLimit(getAcumField(r, 'UpperLimit')) : null,
    lowerLimit: normalRange ? toLimit(getAcumField(r, 'LowerLimit')) : null,
    normalRange: normalRange || undefined,
  });
}

/**
 * Convierte el array plano de wspi_Search_Cumu en el payload para gráficas de evolución.
 * Excluye filas que son títulos de grupo (ResType 10, 11, 12).
 */
export function rowsToEvolutionPayload(
  rows: IAcumuladosRow[],
  patient: IPatientHistory
): EvolutionChartsPayload {
  const reqByCol = new Map<
    number,
    { requisNr: string; extRequisNr: string; entryDate: string }
  >();
  const testMeta = new Map<
    number,
    { testNr: number; testName: string; units: string; printSeq: number }
  >();
  const pointMap = new Map<
    string,
    { result: number | null; upperLimit: number | null; lowerLimit: number | null; normalRange?: string }
  >();

  for (const r of rows) {
    processOneRow(r, reqByCol, testMeta, pointMap);
  }

  const cols = Array.from(reqByCol.entries()).sort((a, b) => a[0] - b[0]);
  const sortedTestEntries = Array.from(testMeta.entries()).sort(
    (a, b) => (a[1].printSeq || a[0]) - (b[1].printSeq || b[0])
  );

  const tests: EvolutionChartTest[] = [];
  for (const [testRow, meta] of sortedTestEntries) {
    const points: EvolutionChartPoint[] = [];
    for (const [requisCol, req] of cols) {
      const cell = pointMap.get(`${testRow}-${requisCol}`);
      if (cell) {
        points.push({
          requestId: req.requisNr,
          externalId: req.extRequisNr,
          dateTime: req.entryDate,
          result: cell.result,
          upperLimit: cell.upperLimit,
          lowerLimit: cell.lowerLimit,
          normalRange: cell.normalRange,
        });
      }
    }
    if (points.length > 0) {
      tests.push({
        testCode: meta.testNr,
        testName: meta.testName,
        unit: meta.units,
        points,
      });
    }
  }

  return {
    patient: buildPatientFromHistory(patient),
    tests,
  };
}
